import { z } from "zod";
import { BookIdSchema, NodeIdSchema } from "./ids";
import { NodeTypeSchema } from "./states";

/** Formatting hints the Chapter Proposal reads (plan "Parsers -> Manuscript contract"). */
export const NodeHintsSchema = z.strictObject({
  styleName: z.string().optional(),
  styleId: z.string().optional(),
  headingLevel: z.int().min(1).max(9).optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
  centred: z.boolean().optional(),
  sizeHalfPoints: z.int().positive().optional(),
  /** Font size relative to the body text; 1.15 or more marks a candidate title. */
  sizeRatio: z.number().positive().optional(),
  listLevel: z.int().nonnegative().optional(),
  ordered: z.boolean().optional(),
  followsPageBreak: z.boolean().optional(),
});
export type NodeHints = z.infer<typeof NodeHintsSchema>;

/**
 * One Manuscript node. `text` keeps *italic* and **bold** markers; Review
 * renders them as formatting and the alignment strips them for the diff.
 */
export const ManuscriptNodeSchema = z.strictObject({
  id: NodeIdSchema,
  ordinal: z.int().nonnegative(),
  type: NodeTypeSchema,
  text: z.string(),
  hints: NodeHintsSchema,
});
export type ManuscriptNode = z.infer<typeof ManuscriptNodeSchema>;

/** GET /books/:id/nodes?from&to: ordinals, `to` exclusive, at most 2,000 per call. */
export const NodesQuerySchema = z
  .strictObject({
    from: z.coerce.number().int().nonnegative(),
    to: z.coerce.number().int().nonnegative(),
  })
  .refine((q) => q.to > q.from && q.to - q.from <= 2000, {
    message: "to must be greater than from by at most 2000",
  });
export type NodesQuery = z.infer<typeof NodesQuerySchema>;

export const NodesResponseSchema = z.strictObject({
  bookId: BookIdSchema,
  from: z.int().nonnegative(),
  to: z.int().nonnegative(),
  /** Number of nodes in the whole Manuscript. */
  total: z.int().nonnegative(),
  nodes: z.array(ManuscriptNodeSchema),
});
export type NodesResponse = z.infer<typeof NodesResponseSchema>;
